import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { formatDistanceToNow, isValid, parseISO } from "date-fns";
import { nb, enUS, pl, sv } from "date-fns/locale";
import {
  DocumentTextIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { useSettings } from "../../../contexts/settings-context";
import { PMDialog } from "../../utils/PMDialog";
import {
  isOfferCalculationV2Enabled,
  resolveOfferContributionMargin,
  resolveOfferContributionMarginPercent,
  resolveOfferCostMetrics,
} from "./utils";
import OfferInquiriesSection from "./offer-inquiries-section";

const EMPTY_VALUE = "—";
const POSITIVE = "#059669";

const KPI_IDS = [
  "cost",
  "contribution_margin",
  "contribution_margin_percent",
  "probability",
];

function dateLocale(i18nLang) {
  if (i18nLang?.startsWith("sv") || i18nLang?.startsWith("se")) return sv;
  if (i18nLang?.startsWith("pl")) return pl;
  if (i18nLang?.startsWith("en")) return enUS;
  return nb;
}

function parseDate(value) {
  if (!value) return null;
  const d = typeof value === "string" ? parseISO(value) : new Date(value);
  return isValid(d) ? d : null;
}

function formatRelativeShort(date, locale, t) {
  if (!date) return EMPTY_VALUE;
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const diffMs = now - date;
  if (diffMs >= 0 && diffMs < dayMs && now.getDate() === date.getDate()) {
    return t("offers.overview.activity.today");
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return t("offers.overview.activity.yesterday");
  }
  const days = Math.floor(diffMs / dayMs);
  if (days > 0 && days < 14) {
    return t("offers.overview.activity.days_ago", { count: days });
  }
  try {
    return formatDistanceToNow(date, { addSuffix: false, locale });
  } catch {
    return EMPTY_VALUE;
  }
}

/**
 * Prefer activity API events; fall back to local revision feed.
 */
function buildActivityEvents(t, offer, revisions, activity, activityOfferId) {
  if (activityOfferId === offer?.id && Array.isArray(activity) && activity.length) {
    return activity.slice(0, 8).map((event) => {
      const meta = event.meta || {};
      const type = event.event_type || "unknown";
      let label = t(`offers.activity.events.${type}.title`, {
        revision_number: meta.revision_number,
        defaultValue: t("offers.activity.events.unknown.title"),
      });
      if (type === "calc_version_created" && meta.calculation_title) {
        label = t("offers.activity.events.calc_version_created.title_named", {
          title: meta.calculation_title,
          defaultValue: label,
        });
      }
      if (type === "import_applied" && meta.filename) {
        label = t("offers.activity.events.import_applied.title_file", {
          filename: meta.filename,
          defaultValue: label,
        });
      }
      const actor = event.actor?.name;
      if (actor) {
        label = t("offers.overview.activity.with_actor", {
          text: label,
          actor,
          defaultValue: `${label} — ${actor}`,
        });
      }
      const linkTab =
        event.meta?.linkTab ||
        event.meta?.link_tab ||
        (type?.startsWith("revision") ? "revisjoner" : null) ||
        (type?.startsWith("calc") || type === "import_applied"
          ? "kalkyle"
          : null) ||
        (type?.startsWith("line_") ||
        type === "lines_generated" ||
        type === "lines_generated_from_calculation" ||
        type === "lines_changed" ||
        type?.startsWith("group_") ||
        type === "groups_changed"
          ? "tilbudslinjer"
          : null) ||
        (type?.startsWith("document_") || type === "template_loaded"
          ? "dokument"
          : null) ||
        (type === "currency_changed" ? "oversikt" : null);
      return {
        id: event.id || `act-${event.occurred_at}`,
        label,
        at: event.occurred_at,
        linkTab,
      };
    });
  }

  const events = [];
  (revisions || []).forEach((revision) => {
    if (!revision) return;
    const statusKey = `offers.history.revision_status.${
      revision.status || "draft"
    }`;
    const statusLabel =
      t(statusKey) === statusKey ? revision.status : t(statusKey);
    events.push({
      id: `revision-${revision.id}`,
      label: t("offers.overview.activity.revision_status", {
        number: revision.revision_number ?? "?",
        status: statusLabel,
      }),
      at: revision.locked_at || revision.sent_at || revision.created_at || null,
      linkTab: "revisjoner",
    });
  });

  if (offer?.converted_to_project_at) {
    events.push({
      id: "converted",
      label: t("offers.overview.activity.converted"),
      at: offer.converted_to_project_at,
      linkTab: null,
    });
  }

  if (offer?.created_at) {
    events.push({
      id: "created",
      label: t("offers.overview.activity.created"),
      at: offer.created_at,
      linkTab: null,
    });
  }

  return events
    .filter((event) => event.at)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
    .slice(0, 8);
}

function resolveProbability(offer) {
  const raw =
    offer?.probability ??
    offer?.win_probability ??
    offer?.likelihood ??
    offer?.chance ??
    null;
  if (raw == null || raw === "") return null;
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return null;
  // Accept 0–1 or 0–100
  return n <= 1 ? n * 100 : n;
}

function KpiCard({
  label,
  value,
  valueColor,
  hint,
  infoAriaLabel,
  onInfoClick,
}) {
  return (
    <div className="rounded-[9px] border border-slate-200 bg-white px-3.5 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] text-slate-400">{label}</div>
        {onInfoClick ? (
          <button
            type="button"
            onClick={onInfoClick}
            aria-label={infoAriaLabel}
            className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-50 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div
        className="mt-0.5 text-[17px] font-[650] tabular-nums"
        style={{ color: valueColor || "#0f172a" }}
      >
        {value}
      </div>
      {hint ? (
        <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/**
 * OfferOverview – Claude KPIs: Kostnad | DB | DG | Sannsynlighet + Aktivitet.
 */
export default function OfferOverview({ offer, featureFlags, isLoading = false }) {
  const { t, i18n } = useTranslation();
  const { priceFormatter } = useSettings();
  const [, setSearchParams] = useSearchParams();
  const locale = dateLocale(i18n.language);
  const [infoKpiId, setInfoKpiId] = useState(null);

  const offerFeatureFlags = useSelector(
    (state) => state.offers?.offerFeatureFlags || {}
  );
  const {
    storeRevisions,
    revisionsOfferId,
    activity,
    activityOfferId,
  } = useSelector((state) => ({
    storeRevisions: state.offers?.revisions || [],
    revisionsOfferId: state.offers?.revisionsOfferId,
    activity: state.offers?.activity || [],
    activityOfferId: state.offers?.activityOfferId,
  }));

  const flags = featureFlags || offer?.offer_feature_flags || offerFeatureFlags || {};
  const v2 = isOfferCalculationV2Enabled(flags);

  const revisions = revisionsOfferId === offer?.id ? storeRevisions : [];

  const formatMoney = useMemo(() => {
    return (amount) => {
      if (amount == null) return EMPTY_VALUE;
      if (priceFormatter?.format) return priceFormatter.format(amount);
      return String(amount);
    };
  }, [priceFormatter]);

  const formatPercent = (amount) => {
    if (amount == null) return EMPTY_VALUE;
    try {
      return `${new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(amount)} %`;
    } catch {
      return `${amount} %`;
    }
  };

  const metrics = useMemo(() => {
    const cost = resolveOfferCostMetrics(offer);
    const margin = resolveOfferContributionMargin(offer);
    const percent = resolveOfferContributionMarginPercent(offer);
    const probability = resolveProbability(offer);

    return {
      costTotal: cost.amount,
      costAuthoritative: cost.authoritative,
      marginValue: margin.amount,
      marginAuthoritative: margin.authoritative,
      percentValue: percent.amount,
      percentAuthoritative: percent.authoritative,
      probability,
    };
  }, [offer]);

  if (isLoading || !offer) {
    return (
      <div className="flex flex-col gap-4 p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((key) => (
            <div
              key={key}
              className="h-[74px] animate-pulse rounded-[9px] border border-slate-200 bg-slate-50"
            />
          ))}
        </div>
        <div className="h-40 animate-pulse rounded-[9px] border border-slate-200 bg-slate-50" />
      </div>
    );
  }

  const authoritativeHint = t("offers.overview.authoritative_hint");
  const estimateHint = t("offers.overview.estimate_hint");
  const activityEvents = buildActivityEvents(
    t,
    offer,
    revisions,
    activity,
    activityOfferId
  );

  const marginPositive =
    metrics.marginValue != null && metrics.marginValue >= 0;
  const percentPositive =
    metrics.percentValue != null && metrics.percentValue >= 0;

  const openTab = (tab) => {
    if (!tab) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: false }
    );
  };

  const openInfo = (kpiId) => setInfoKpiId(kpiId);
  const closeInfo = () => setInfoKpiId(null);
  const infoTitleKey =
    infoKpiId && KPI_IDS.includes(infoKpiId)
      ? `offers.overview.kpi.${infoKpiId}`
      : undefined;

  return (
    <div className="flex flex-col gap-4 overflow-auto p-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label={t("offers.overview.kpi.cost")}
          value={formatMoney(metrics.costTotal)}
          hint={
            metrics.costTotal == null
              ? undefined
              : metrics.costAuthoritative
                ? v2
                  ? authoritativeHint
                  : undefined
                : estimateHint
          }
          infoAriaLabel={t("offers.overview.kpi.cost.info_aria")}
          onInfoClick={() => openInfo("cost")}
        />
        <KpiCard
          label={t("offers.overview.kpi.contribution_margin")}
          value={formatMoney(metrics.marginValue)}
          valueColor={
            metrics.marginValue == null
              ? undefined
              : marginPositive
                ? POSITIVE
                : "#DC2626"
          }
          hint={
            metrics.marginValue == null
              ? undefined
              : metrics.marginAuthoritative
                ? v2
                  ? authoritativeHint
                  : undefined
                : estimateHint
          }
          infoAriaLabel={t("offers.overview.kpi.contribution_margin.info_aria")}
          onInfoClick={() => openInfo("contribution_margin")}
        />
        <KpiCard
          label={t("offers.overview.kpi.contribution_margin_percent")}
          value={formatPercent(metrics.percentValue)}
          valueColor={
            metrics.percentValue == null
              ? undefined
              : percentPositive
                ? POSITIVE
                : "#DC2626"
          }
          hint={
            metrics.percentValue == null
              ? undefined
              : metrics.percentAuthoritative
                ? v2
                  ? authoritativeHint
                  : undefined
                : estimateHint
          }
          infoAriaLabel={t(
            "offers.overview.kpi.contribution_margin_percent.info_aria"
          )}
          onInfoClick={() => openInfo("contribution_margin_percent")}
        />
        <KpiCard
          label={t("offers.overview.kpi.probability")}
          value={
            metrics.probability == null
              ? EMPTY_VALUE
              : formatPercent(metrics.probability)
          }
          hint={
            metrics.probability == null
              ? t("offers.overview.kpi.probability_empty_hint")
              : undefined
          }
          infoAriaLabel={t("offers.overview.kpi.probability.info_aria")}
          onInfoClick={() => openInfo("probability")}
        />
      </div>

      {metrics.marginValue != null && metrics.marginValue < 0 ? (
        <div className="rounded-[7px] border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
          {t("offers.overview.negative_margin")}
        </div>
      ) : null}

      <OfferInquiriesSection offer={offer} featureFlags={flags} />

      <div className="rounded-[9px] border border-slate-200 bg-white p-4">
        <h3 className="mb-2.5 text-[13px] font-[650] text-slate-900">
          {t("offers.overview.activity.title")}
        </h3>
        {activityEvents.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-slate-500">
            <DocumentTextIcon
              className="h-8 w-8 text-slate-300"
              aria-hidden="true"
            />
            <p className="text-sm">{t("offers.overview.activity.empty")}</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-2.5 text-[12.5px]">
            {activityEvents.map((event) => {
              const d = parseDate(event.at);
              const row = (
                <>
                  <span className="w-20 shrink-0 text-slate-400">
                    {formatRelativeShort(d, locale, t)}
                  </span>
                  <span className="min-w-0 text-slate-800">{event.label}</span>
                </>
              );
              if (event.linkTab) {
                return (
                  <li key={event.id}>
                    <button
                      type="button"
                      onClick={() => openTab(event.linkTab)}
                      className="flex w-full gap-2.5 text-left hover:opacity-80"
                    >
                      {row}
                    </button>
                  </li>
                );
              }
              return (
                <li key={event.id} className="flex gap-2.5">
                  {row}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <PMDialog
        open={Boolean(infoKpiId)}
        onClose={closeInfo}
        closeOnOutsideClick
        size="sm"
        title={infoTitleKey}
      >
        {infoKpiId ? (
          <p className="text-sm leading-relaxed text-slate-600">
            {t(`offers.overview.kpi.${infoKpiId}.info_body`)}
          </p>
        ) : null}
      </PMDialog>
    </div>
  );
}
